import React, {useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Modal} from 'components/ui-kit/Modal';
import {Select} from 'components/ui-kit/Select';

import {
    $invoiceStatusChangeModalHandler,
    changeInvoiceStatusFx,
    setInvoiceStatusChangeModalHandler,
} from 'models/profile/billingIvoice';

import {BILLING_INVOICE_STATUS} from 'types/billing';
import {mapArrayToI18nOptions} from 'utils/select';

import {
    ArrowContainer,
    ArrowText,
    ButtonsContainer,
    CheckboxText,
    ConfirmDescription,
    FieldContainer,
    FieldsContainer,
    Form,
    ModalContainer,
    ModalContainerSuccess,
    NoteContainer,
    NoteText,
} from './styled';

type ModalState = 'form' | 'confirm';

interface FormData {
    status: Record<string, any>;
}

export const InvoiceStatusChangeModal: React.FC = () => {
    const {t} = useTranslation();
    const [state, setState] = useState<ModalState>('form');
    const invoiceStatusChangeModalHandler = useStore($invoiceStatusChangeModalHandler);
    const pending = useStore(changeInvoiceStatusFx.pending);

    const setTitle = useMemo(() => {
        switch (state) {
            case 'form':
                return t('pages.profile.billing.invoiceStatusChange.titleForm');
            case 'confirm':
                return t('pages.profile.billing.invoiceStatusChange.titleConfirm');
        }
    }, [t, state]);

    const defaultStatus = useMemo(
        () =>
            mapArrayToI18nOptions(
                BILLING_INVOICE_STATUS.map((item) => ({
                    key: item,
                    value: item,
                })),
                'dictionaries.billingInvoiceStatus',
            )[0],
        [t],
    );

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                status: yup
                    .object({
                        label: yup.string(),
                        value: yup.string(),
                    })
                    .nullable()
                    .test(
                        'not-default',
                        t('pages.profile.billing.invoiceStatusChange.error.sameStatus'),
                        (value) => {
                            if (!value || !defaultStatus) return true;
                            return value.value !== defaultStatus.value;
                        },
                    ),
            }),
        [t, defaultStatus],
    );

    const {
        control,
        handleSubmit,
        formState: {isValid},
        reset,
    } = useForm<FormData>({
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            status: defaultStatus,
        },
    });

    const onSubmit = async (data: FormData) => {
        const [sent] = BILLING_INVOICE_STATUS;
        if (data.status.value === sent) {
            return;
        }

        setState('confirm');
    };

    const handleConfirm = () => {
        if (!invoiceStatusChangeModalHandler.data) {
            return;
        }

        changeInvoiceStatusFx(invoiceStatusChangeModalHandler.data);
    };

    const handleClose = () => {
        setInvoiceStatusChangeModalHandler({
            show: false,
            data: null,
        });
    };

    useEffect(() => {
        if (!invoiceStatusChangeModalHandler.show) {
            setState('form');
            reset();
        }
    }, [invoiceStatusChangeModalHandler]);

    const renderForm = () => (
        <ModalContainer>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <FieldsContainer>
                    <FieldContainer>
                        <Controller
                            control={control}
                            name="status"
                            render={(props) => (
                                <Select
                                    {...props}
                                    options={mapArrayToI18nOptions(
                                        BILLING_INVOICE_STATUS.map((item) => ({
                                            key: item,
                                            value: item,
                                        })),
                                        'dictionaries.billingInvoiceStatus',
                                    )}
                                />
                            )}
                        />
                    </FieldContainer>
                    <NoteContainer>
                        <HtmlText
                            Tag={NoteText as React.ElementType}
                            __html={t('pages.profile.billing.invoiceStatusChange.note')}
                        />
                    </NoteContainer>
                </FieldsContainer>
                <ButtonsContainer>
                    <Button variant={ButtonVariant.Outline} type="button" onClick={handleClose}>
                        {t('pages.profile.billing.invoiceStatusChange.buttons.cancel')}
                    </Button>
                    <Button variant={ButtonVariant.Normal} type="submit" disabled={!isValid}>
                        {t('pages.profile.billing.invoiceStatusChange.buttons.save')}
                    </Button>
                </ButtonsContainer>
            </Form>
        </ModalContainer>
    );

    const renderConfirm = () => (
        <ModalContainerSuccess>
            <HtmlText
                Tag={ConfirmDescription as React.ElementType}
                __html={t('pages.profile.billing.invoiceStatusChange.confirmDescription')}
            />
            <ArrowContainer>
                <CheckboxText>
                    {t('pages.profile.billing.invoiceStatusChange.arrow.unpaid')}
                </CheckboxText>
                <ArrowText>→</ArrowText>
                <CheckboxText>
                    {t('pages.profile.billing.invoiceStatusChange.arrow.paid')}
                </CheckboxText>
            </ArrowContainer>
            <ButtonsContainer>
                <Button
                    disabled={pending}
                    variant={ButtonVariant.Outline}
                    type="button"
                    onClick={handleClose}
                >
                    {t('pages.profile.billing.invoiceStatusChange.buttons.cancel')}
                </Button>
                <Button
                    disabled={pending}
                    variant={ButtonVariant.Normal}
                    type="button"
                    onClick={handleConfirm}
                >
                    {t('pages.profile.billing.invoiceStatusChange.buttons.confirm')}
                </Button>
            </ButtonsContainer>
        </ModalContainerSuccess>
    );

    return (
        <Modal
            title={setTitle}
            show={invoiceStatusChangeModalHandler.show}
            modalWidth={0}
            onClose={handleClose}
            hideCloseButton={false}
            closable={!pending}
        >
            {state === 'form' && renderForm()}
            {state === 'confirm' && renderConfirm()}
        </Modal>
    );
};
